import { TableFooterWithCount } from '@plato/ui-legacy/components/tables/table-footer-with-count';
import { useQueryParam } from '@plato/ui-legacy/lib/hooks/use-query-param';
import { jsonParam } from '@plato/ui-legacy/lib/query-params';
import type { ColumnSort } from '@plato/ui/components/data-table';
import { DataTable } from '@plato/ui/components/data-table';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { TaskCreatedSource } from '../../analytics/types/analytics-events.ts';
import { useUserOverviewColumns, type UserOverviewSortBy } from '../columns/use-user-overview-columns.tsx';
import { useTeamFilters } from '../hooks/use-team-filters.ts';

const sortingParam = jsonParam<ColumnSort<UserOverviewSortBy>>();

const defaultSorting: ColumnSort<UserOverviewSortBy> = { by: 'customerContacts', direction: 'desc' };

type Props = {
    /** Task-creation source for the per-row New Task button. */
    taskSource?: TaskCreatedSource;
};

export function UserOverviewTable({ taskSource = 'team_user' }: Props) {
    const { t } = useTranslation();
    const columns = useUserOverviewColumns(taskSource);
    const [sorting, setSorting] = useQueryParam('userSort', sortingParam, defaultSorting);
    const sendEvent = useAnalytics();

    const { appliedFilters } = useTeamFilters();

    const { data, isLoading } =
        api.platoUser.query.users.getManyUsers.getUserVisitsOverview.useQuery(appliedFilters);

    return (
        <>
            <DataTable
                emptyLabel={t('empty_table__no_results')}
                isLoading={isLoading}
                columns={columns}
                data={data ?? []}
                manualSorting={false}
                sorting={sorting}
                onSortingChange={(sort, columnId) => {
                    setSorting(sort);
                    if (sort && columnId) {
                        sendEvent('team_page_sort_used', {
                            column: columnId,
                            direction: sort.direction === 'desc' ? 'DESC' : 'ASC',
                        });
                    }
                }}
            />
            <TableFooterWithCount loading={isLoading} count={data?.length} />
        </>
    );
}
